"use client";
import { Translate } from "@/components/Translate";
import { sizes } from "@/components/bloks/Asset";
import { Carousel } from "@/components/ui/Carousel";
import { ProductCardWithCampaigns } from "@/features/campaigns";
import { useTranslations } from "@/i18n/client";
import type { ProductCard as IProductCard } from "@/lib/centra";
import { useRecommendationClick } from "@/lib/depict/useRecommendationClick";
import { useToken } from "@frend-digital/centra/client";
import { clsx } from "@frend-digital/ui";
import { useQuery } from "@tanstack/react-query";
import { Suspense } from "react";
import { rpc } from "#rpc";
import styles from "./cart.module.css";
import { useCart } from "./hooks";

export const CartUpsell = ({ className }: { className?: string }) => {
	const { data: cartData } = useCart();

	const token = useToken();

	const productIds =
		cartData?.items?.map((item) => item?.item?.split("-")[1]).filter(Boolean) ?? [];

	const { data: products } = useQuery({
		queryKey: ["cart-depict-recommendations", productIds],
		queryFn: async () => {
			const cartRecommendations = await rpc.depict.recommendations({
				product_ids: productIds,
				type: "after_basket",
				limit: 8,
				token: token!,
			});

			return cartRecommendations;
		},
		enabled: !!productIds.length && !!token,
	});

	const handleProductClick = useRecommendationClick(products, "cart_upsell");

	if (!products) return null;

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: keyboard navigation handled by individual product cards
		<div className={clsx(styles.cartUpsell, className)} onClick={handleProductClick}>
			<Suspense fallback>
				<UnsuspendedCartUpsell products={products} />
			</Suspense>
		</div>
	);
};

const UnsuspendedCartUpsell = ({ products }: { products: (IProductCard | undefined)[] }) => {
	const t = useTranslations();
	return (
		<>
			<Carousel
				showControls={true}
				className={styles.carousel}
				title={t("You might also like")}
				titleStyle={styles.upsellTitle}
			>
				{products?.map((product) => {
					return (
						<ProductCardWithCampaigns
							sizes={sizes(75, 25)}
							key={product?.displayItemId}
							product={product!}
							displayVariants={false}
							showRatings
						/>
					);
				})}
			</Carousel>
		</>
	);
};
